import rpointInPolygon from 'robust-point-in-polygon';
import { Shape, Vector2, Vector2Tuple } from 'three';

import { FAreaNode, FNodeTypesEnum } from '@spazious/dollhouse-fsetup';
import { useStore } from '@spazious/dollhouse-store';
import { toVector2 } from '@spazious/dollhouse-three';
import { useEventCallback } from '@spazious/react-hooks';

import { useEvents } from '../components/index.js';
import { DollhouseMessage, MainViewMessage } from '../core/EventManager.js';

const pointsToPolygon = (finalPoints: Vector2Tuple[]): Vector2Tuple[][] => {
  const mapFn = (v: Vector2) => v.toArray();
  const shape = new Shape(finalPoints.map((p) => new Vector2().fromArray(p)));
  const points = shape.getPoints().map(mapFn);
  const holePoints = shape.getPointsHoles(12).map((v) => v.map(mapFn));
  const polygon = [points, ...holePoints];

  return polygon;
};

const pointInPolygon = (polygon: Vector2Tuple[][], point: Vector2Tuple): 0 | 1 | -1 => {
  const [points, ...holes] = polygon;

  const parsedPoint = toVector2(point).toArray();

  const result = rpointInPolygon(points, parsedPoint);

  if (result === 1) {
    for (let i = 0; i < holes.length; i++) {
      const hole = holes[i];
      const holeResult = rpointInPolygon(hole, parsedPoint);
      if (holeResult < 1) {
        return (1 - holeResult) as 0 | 1 | -1;
      }
    }

    return 1;
  }

  return result;
};

const polygonsIntersect = (polygon1: Vector2Tuple[][], polygon2: Vector2Tuple[][]): boolean => {
  const [points1] = polygon1;
  const [points2] = polygon2;
  const intersect =
    points1.some((p) => pointInPolygon(polygon2, p) !== 1) ||
    points2.some((p) => pointInPolygon(polygon1, p) !== 1);
  return intersect;
};

export const useRestrictedArea = () => {
  const events = useEvents();

  const fareaNodes = useStore(({ graph }) =>
    graph.children.filter((node) => node.type === FNodeTypesEnum.FArea)
  ) as FAreaNode[];

  const isPointInRestrictedArea = useEventCallback((point: Vector2Tuple): boolean => {
    return fareaNodes.some((node) => {
      const closedLoop = [...node.points, node.points[0]];
      return pointInPolygon(pointsToPolygon(closedLoop), point) !== 1;
    });
  });

  const isPolygonInRestrictedArea = useEventCallback((polygon: Vector2Tuple[][]): boolean => {
    return fareaNodes.some((node) => {
      const closedLoop = [...node.points, node.points[0]];
      return polygonsIntersect(pointsToPolygon(closedLoop), polygon);
    });
  });

  const confirm = useEventCallback((onConfirmResponse: (message: MainViewMessage) => void) => {
    const cb = (message: MainViewMessage) => {
      events.off('mainViewMessage', cb);
      onConfirmResponse(message);
    };
    events.on('mainViewMessage', cb);
    events.emit('dollhouseMessage', DollhouseMessage.RestrictedAreaEntered);
  });

  return {
    isPointInRestrictedArea,
    isPolygonInRestrictedArea,
    confirm,
  };
};
